import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { MODULE_LOADING_MESSAGES } from 'src/app/private/services/module.config';
import { CommCardData } from 'src/app/utils/communication-log.util';
import { MeetingCardData } from 'src/app/utils/meeting-booking.util';

export { MeetingCardData };

export interface TicketCardData {
  ticketNo: string;
  title: string;
  status: string;
  priority: string;
  source: string;
  description: string;
}

export interface LeadActivityFeedItem {
  id?: string | number;
  actorName: string;
  createdAt: string | Date;
  body: string;
  meetingData?: MeetingCardData;
  ticketData?: TicketCardData;
  commData?: CommCardData;
  raw?: any;
}

@Component({
  selector: 'app-lead-activity-feed',
  templateUrl: './lead-activity-feed.component.html',
  styleUrls: ['./lead-activity-feed.component.scss'],
})
export class LeadActivityFeedComponent implements OnChanges, OnDestroy {
  @Input() title = 'Activity Timeline';
  @Input() items: LeadActivityFeedItem[] = [];
  @Input() loading = false;
  @Input() showAdd = false;
  @Input() addLabel = 'Add';
  @Input() showActions = true;
  @Input() emptyText = 'No records yet.';

  @Output() add = new EventEmitter<void>();
  @Output() edit = new EventEmitter<LeadActivityFeedItem>();
  @Output() delete = new EventEmitter<LeadActivityFeedItem>();

  // Loading message rotation fields
  feedActiveMessages: string[] = [];
  feedCurrentMessageIndex = 0;
  feedCurrentMessage = '';
  feedFadeState: 'fade-out' | 'fade-in' | '' = '';
  private feedMessageInterval: any = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading']) {
      if (this.loading) {
        this.startFeedMessageRotation();
      } else {
        this.cleanupFeedMessageRotation();
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanupFeedMessageRotation();
  }

  private startFeedMessageRotation() {
    if (this.feedMessageInterval) return; // Prevent multiple intervals
    this.feedActiveMessages = this.getMessagesForTitle(this.title);
    this.feedCurrentMessageIndex = 0;
    this.feedCurrentMessage = this.feedActiveMessages[this.feedCurrentMessageIndex] || 'Loading...';
    this.feedFadeState = '';

    this.feedMessageInterval = setInterval(() => {
      this.feedFadeState = 'fade-out';
      setTimeout(() => {
        if (!this.loading) {
          this.cleanupFeedMessageRotation();
          return;
        }
        this.feedCurrentMessageIndex = (this.feedCurrentMessageIndex + 1) % this.feedActiveMessages.length;
        this.feedCurrentMessage = this.feedActiveMessages[this.feedCurrentMessageIndex];
        this.feedFadeState = 'fade-in';
        setTimeout(() => {
          this.feedFadeState = '';
        }, 200);
      }, 200);
    }, 2000);
  }

  private cleanupFeedMessageRotation() {
    if (this.feedMessageInterval) {
      clearInterval(this.feedMessageInterval);
      this.feedMessageInterval = null;
    }
  }

  private getMessagesForTitle(title: string): string[] {
    const t = String(title || '').toLowerCase();
    if (t.includes('ticket') || t.includes('support')) {
      return MODULE_LOADING_MESSAGES['manage-tickets'] || MODULE_LOADING_MESSAGES['generic'] || [];
    }
    if (t.includes('transaction') || t.includes('invoice') || t.includes('billing')) {
      return MODULE_LOADING_MESSAGES['invoices'] || MODULE_LOADING_MESSAGES['generic'] || [];
    }
    if (t.includes('analytics') || t.includes('forecast')) {
      return MODULE_LOADING_MESSAGES['forecast'] || MODULE_LOADING_MESSAGES['generic'] || [];
    }
    if (t.includes('task') || t.includes('project')) {
      return MODULE_LOADING_MESSAGES['manage-projects'] || MODULE_LOADING_MESSAGES['generic'] || [];
    }
    if (t.includes('lead') || t.includes('follow')) {
      return MODULE_LOADING_MESSAGES['leads'] || MODULE_LOADING_MESSAGES['generic'] || [];
    }
    return MODULE_LOADING_MESSAGES['generic'] || [];
  }

  initials(name: string): string {
    const parts = (name || 'U').split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toLowerCase();
    }
    return (parts[0]?.[0] || 'u').toLowerCase();
  }

  formatWhen(value: string | Date): string {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
